import { Component, Director, director, Node, v4, Vec4, _decorator } from 'cc';

const { ccclass, property, executeInEditMode } = _decorator;

@ccclass('ContentPadding')
@executeInEditMode
export default class ContentPadding extends Component {
    @property(Node)
    private _content: Node = null;

    @property({ formerlySerializedAs: '_constrait' })
    private readonly _constraint: Vec4 = v4(-1, -1, -1, -1);

    @property
    private readonly _padding: Vec4 = v4();

    @property
    private _affectedByScale: boolean = false;

    private _dirtyContentSize = false;
    private _dirtySelfSize = false;
    private _dirtySettings = false;

    @property(Node)
    public get content() {
        return this._content;
    }
    public set content(value: Node) {
        if (this._content === value) {
            return;
        }
        if (this._content) {
            this._unregisterContentEvent(this._content);
        }
        this._content = value;
        this._applyContent(value);
    }

    @property
    public get paddingLeft() {
        return this._padding.x;
    }
    public set paddingLeft(value: number) {
        if (this._padding.x === value) {
            return;
        }
        this._padding.x = value;
        this._onSettingsChanged();
    }

    @property
    public get paddingBottom() {
        return this._padding.y;
    }
    public set paddingBottom(value: number) {
        if (this._padding.y === value) {
            return;
        }
        this._padding.y = value;
        this._onSettingsChanged();
    }

    @property
    public get paddingRight() {
        return this._padding.z;
    }
    public set paddingRight(value: number) {
        if (this._padding.z === value) {
            return;
        }
        this._padding.z = value;
        this._onSettingsChanged();
    }

    @property
    public get paddingTop() {
        return this._padding.w;
    }
    public set paddingTop(value: number) {
        if (this._padding.w === value) {
            return;
        }
        this._padding.w = value;
        this._onSettingsChanged();
    }

    @property({ min: -1 })
    public get minWidth() {
        return this._constraint.x;
    }
    public set minWidth(value: number) {
        value = Math.max(-1, value);
        if (this._constraint.x === value) {
            return;
        }
        this._constraint.x = value;
        this._onSettingsChanged();
    }

    @property({ min: -1 })
    public get minHeight() {
        return this._constraint.y;
    }
    public set minHeight(value: number) {
        value = Math.max(-1, value);
        if (this._constraint.y === value) {
            return;
        }
        this._constraint.y = value;
        this._onSettingsChanged();
    }

    @property({ min: -1 })
    public get maxWidth() {
        return this._constraint.z;
    }
    public set maxWidth(value: number) {
        value = Math.max(-1, value);
        if (this._constraint.z === value) {
            return;
        }
        this._constraint.z = value;
        this._onSettingsChanged();
    }

    @property({ min: -1 })
    public get maxHeight() {
        return this._constraint.w;
    }
    public set maxHeight(value: number) {
        value = Math.max(-1, value);
        if (this._constraint.w === value) {
            return;
        }
        this._constraint.w = value;
        this._onSettingsChanged();
    }

    @property
    public get affectedByScale() {
        return this._affectedByScale;
    }
    public set affectedByScale(value: boolean) {
        if (this._affectedByScale === value) {
            return;
        }
        this._affectedByScale = value;
        this._onSettingsChanged();
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected __preload() {
        this._applyContent(this.content);
    }

    protected onEnable() {
        director.on(Director.EVENT_AFTER_UPDATE, this._updateContentSize, this);
        this.node.on(Node.EventType.SIZE_CHANGED, this._onSelfSizeChanged, this);
        this._doUpdateContentSize();
    }

    protected onDisable() {
        director.off(Director.EVENT_AFTER_UPDATE, this._updateContentSize, this);
        this.node.off(Node.EventType.SIZE_CHANGED, this._onSelfSizeChanged, this);
    }

    private _applyContent(content: Node) {
        if (!content) {
            return;
        }
        this._registerContentEvent(content);
        this._doUpdateContentSize();
    }

    private _registerContentEvent(content: Node) {
        content.on(Node.EventType.SIZE_CHANGED, this._onContentSizeChanged, this);
    }

    private _unregisterContentEvent(content: Node) {
        content.off(Node.EventType.SIZE_CHANGED, this._onContentSizeChanged, this);
    }

    private _onContentSizeChanged() {
        this._dirtyContentSize = true;
    }

    private _onSelfSizeChanged() {
        this._dirtySelfSize = true;
    }

    private _onSettingsChanged() {
        this._dirtySettings = true;
    }

    private _updateContentSize() {
        if (this._dirtyContentSize || this._dirtySelfSize || this._dirtySettings) {
            this._doUpdateContentSize();
            this._dirtyContentSize = false;
            this._dirtySelfSize = false;
            this._dirtySettings = false;
        }
    }

    private _doUpdateContentSize() {
        if (!this.content) {
            return;
        }
        const { x: paddingLeft, y: paddingBottom, z: paddingRight, w: paddingTop } = this._padding;
        const { x: minWidth, y: minHeight, z: maxWidth, w: maxHeight } = this._constraint;
        let targetWidth = this.content.transformWidth * (this._affectedByScale ? this.content.scaleX : 1) + paddingLeft + paddingRight;
        let targetHeight = this.content.transformHeight * (this._affectedByScale ? this.content.scaleY : 1) + paddingTop + paddingBottom;
        if (minWidth >= 0) {
            targetWidth = Math.max(minWidth, targetWidth);
        }
        if (minHeight >= 0) {
            targetHeight = Math.max(minHeight, targetHeight);
        }
        if (maxWidth >= 0) {
            targetWidth = Math.min(maxWidth, targetWidth);
        }
        if (maxHeight >= 0) {
            targetHeight = Math.min(maxHeight, targetHeight);
        }
        this.node.transform.setContentSize(targetWidth, targetHeight);
    }
}
